'use client';

import * as React from 'react';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  DialogCloseGlyph,
  dialogCloseToneClassName,
  type DialogBackdrop,
  type DialogCloseButtonVariant,
  type DialogSurface,
} from '@/hen-ui/shadcn/dialog';
import {
  Drawer as ShadcnDrawer,
  DrawerClose as ShadcnDrawerClose,
  DrawerContent as ShadcnDrawerContent,
} from '@/hen-ui/shadcn/drawer';
import { cn } from '@/hen-ui/utils';
import {
  modalSheetVariants,
  type ModalContentVariantProps,
} from './modal.variants';
import { useOpenerFocusReturn } from './useOpenerFocusReturn';

export interface ModalSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modal?: boolean;
  showCloseButton?: boolean;
  /** Colocated CTAs, already wrapped in `DrawerTrigger`. */
  trigger?: React.ReactNode;
  /** Pinned title row that also names the sheet. */
  title?: string;
  rounded?: ModalContentVariantProps['rounded'];
  closeButtonVariant?: DialogCloseButtonVariant | null;
  surface?: DialogSurface | null;
  backdrop?: DialogBackdrop | null;
  /** Classes for the sticky title row, from `modalHeaderVariants`. */
  headerClassName?: string;
  /** Classes for the body wrapper, from `modalBodyVariants`. */
  bodyClassName?: string;
  /** Rises to just below the top of the screen whatever the content height. */
  tall?: boolean;
  /** Draws the pull bar on the regular sheet. Ignored when `tall`. */
  showHandle?: boolean;
  children?: React.ReactNode;
}

/** Bottom-sheet Modal: swipe-down closes it, with or without the pull bar.
 * The tall side-panel sheet is not draggable; X, outside tap, or Escape closes it. */
export function ModalSheet({
  open,
  onOpenChange,
  modal,
  showCloseButton = true,
  trigger,
  title,
  rounded,
  closeButtonVariant,
  surface,
  backdrop,
  headerClassName,
  bodyClassName,
  tall = false,
  showHandle = false,
  children,
}: ModalSheetProps) {
  const isDark = surface === 'dark';
  const showsHandle = !tall && showHandle;
  // With no handle or title the top row holds only Close: float it over the corner
  // so the content header starts at the sheet edge and its text clears the button.
  const floatsClose =
    !showsHandle && (title == null || title === '') && showCloseButton;
  const closeVariant = closeButtonVariant ?? 'plain';
  const focusReturn = useOpenerFocusReturn();

  return (
    <ShadcnDrawer
      direction='bottom'
      open={open}
      onOpenChange={onOpenChange}
      modal={modal}
      // With no pull bar drawn, `handleOnly` blocks every drag, so the tall
      // sheet cannot be swiped closed. Explicit close paths stay available.
      handleOnly={tall}
      autoFocus
    >
      {trigger}
      <ShadcnDrawerContent
        data-hen-id={COMPONENT_IDS.Modal}
        aria-describedby={undefined}
        className={cn(
          'border-transparent',
          modalSheetVariants({ rounded }),
          tall &&
            'data-[vaul-drawer-direction=bottom]:top-2xl data-[vaul-drawer-direction=bottom]:mt-0 data-[vaul-drawer-direction=bottom]:max-h-full md:data-[vaul-drawer-direction=bottom]:top-0',
          floatsClose &&
            '[&>[data-slot=drawer-top-row]]:absolute [&>[data-slot=drawer-top-row]]:right-0 [&>[data-slot=drawer-top-row]]:top-0 [&>[data-slot=drawer-top-row]]:z-20 [&>[data-slot=drawer-top-row]]:w-auto [&_[data-slot=dialog-header]]:pr-[4.5rem]',
        )}
        surface={surface}
        backdrop={backdrop}
        headerTitle={title}
        headerClassName={headerClassName}
        bodyClassName={bodyClassName}
        showHandle={showsHandle}
        onOpenAutoFocus={focusReturn.onOpenAutoFocus}
        onCloseAutoFocus={focusReturn.onCloseAutoFocus}
        bottomBarEnd={
          showCloseButton ? (
            <ShadcnDrawerClose
              aria-label='Close'
              className={cn(
                'shrink-0 rounded-max p-1',
                closeVariant === 'circle' &&
                  // 44px target without making the title row taller.
                  'inline-flex min-h-11 min-w-11 -my-1.5 -mr-1.5 items-center justify-center',
                dialogCloseToneClassName(closeVariant, isDark),
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-focus',
              )}
            >
              <DialogCloseGlyph variant={closeVariant} dark={isDark} />
            </ShadcnDrawerClose>
          ) : undefined
        }
      >
        {children}
      </ShadcnDrawerContent>
    </ShadcnDrawer>
  );
}
